/**
 * Base-level Sequence field settings (pattern + next).
 * Stored on `_bases/{baseKey}.sequences` as a map keyed by `TYPEKEY.fieldName`.
 */

import {
  DEFAULT_SEQUENCE_PATTERN,
  buildSequenceSettingsKey,
  normalizeSequencePattern,
  parseSequencePattern,
  resolveSequencePattern,
} from 'utilities/SequencePattern';

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeNext(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.floor(n);
}

/**
 * @param {object|null|undefined} raw
 * @returns {Record<string, { pattern: string, next: number }>}
 */
export function normalizeSequenceSettingsMap(raw) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const out = {};

  Object.entries(source).forEach(([key, entry]) => {
    const settingsKey = normalizeString(key);
    if (!settingsKey || !entry || typeof entry !== 'object' || Array.isArray(entry)) return;
    const pattern = normalizeSequencePattern(entry.pattern) || DEFAULT_SEQUENCE_PATTERN;
    out[settingsKey] = {
      pattern,
      next: normalizeNext(entry.next),
    };
  });

  return out;
}

/**
 * @param {object|null|undefined} base
 * @returns {Record<string, { pattern: string, next: number }>}
 */
export function getSequenceSettingsMap(base) {
  return normalizeSequenceSettingsMap(base?.sequences);
}

/**
 * Discover Sequence fields from node type definitions.
 *
 * @param {object[]} typeDefs
 * @returns {Array<{
 *   settingsKey: string,
 *   typeKey: string,
 *   typeLabel: string,
 *   fieldName: string,
 *   fieldLabel: string,
 *   defaultPattern: string,
 *   schemaField: object,
 * }>}
 */
export function listSequenceFieldsFromTypes(typeDefs = []) {
  const rows = [];
  const seen = new Set();

  (Array.isArray(typeDefs) ? typeDefs : []).forEach((typeDef) => {
    const typeKey = normalizeString(typeDef?.key).toUpperCase();
    if (!typeKey) return;

    const typeLabel = normalizeString(typeDef?.label || typeDef?.title || typeKey) || typeKey;
    const fields = Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : [];

    fields.forEach((field) => {
      const fieldType = normalizeString(field?.type).toLowerCase();
      const fieldName = normalizeString(field?.name);
      if (fieldType !== 'sequence' || !fieldName) return;

      const settingsKey = buildSequenceSettingsKey(typeKey, fieldName);
      if (!settingsKey || seen.has(settingsKey)) return;
      seen.add(settingsKey);

      rows.push({
        settingsKey,
        typeKey,
        typeLabel,
        fieldName,
        fieldLabel: normalizeString(field?.label) || fieldName,
        defaultPattern: normalizeSequencePattern(field?.defaultPattern) || DEFAULT_SEQUENCE_PATTERN,
        schemaField: field,
      });
    });
  });

  rows.sort((a, b) => {
    const byType = a.typeLabel.localeCompare(b.typeLabel);
    if (byType !== 0) return byType;
    return a.fieldLabel.localeCompare(b.fieldLabel);
  });

  return rows;
}

/**
 * Build editable draft rows for Settings UI.
 *
 * @param {object|null|undefined} base
 * @param {object[]} typeDefs
 * @returns {Array<object>}
 */
export function buildSequenceSettingsRows(base, typeDefs = []) {
  const settingsMap = getSequenceSettingsMap(base);
  return listSequenceFieldsFromTypes(typeDefs).map((row) => {
    const stored = settingsMap[row.settingsKey] || null;
    const pattern = resolveSequencePattern(stored, row.schemaField);
    return {
      ...row,
      pattern,
      next: stored ? normalizeNext(stored.next) : 1,
    };
  });
}

/**
 * @param {Array<{ settingsKey: string, pattern: string, next: number|string }>} rows
 * @returns {{ sequences: Record<string, { pattern: string, next: number }> }}
 */
export function buildSequenceSettingsPatch(rows = []) {
  const sequences = {};

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const settingsKey = normalizeString(row?.settingsKey);
    if (!settingsKey) return;
    const pattern = normalizeSequencePattern(row?.pattern) || DEFAULT_SEQUENCE_PATTERN;
    sequences[settingsKey] = {
      pattern,
      next: normalizeNext(row?.next),
    };
  });

  return { sequences };
}

/**
 * @param {Array<{ settingsKey: string, pattern: string, next: number }>} a
 * @param {Array<{ settingsKey: string, pattern: string, next: number }>} b
 * @returns {boolean}
 */
export function sequenceSettingsRowsEqual(a, b) {
  const left = Array.isArray(a) ? a : [];
  const right = Array.isArray(b) ? b : [];
  if (left.length !== right.length) return false;

  for (let i = 0; i < left.length; i += 1) {
    if (left[i].settingsKey !== right[i].settingsKey) return false;
    if (normalizeSequencePattern(left[i].pattern) !== normalizeSequencePattern(right[i].pattern)) {
      return false;
    }
    if (normalizeNext(left[i].next) !== normalizeNext(right[i].next)) return false;
  }

  return true;
}

/**
 * Validate a draft row pattern.
 *
 * @param {string} pattern
 * @returns {string} empty when valid
 */
export function validateSequencePatternInput(pattern) {
  const normalized = normalizeSequencePattern(pattern);
  if (!normalized) return 'Pattern is required.';
  const parsed = parseSequencePattern(normalized);
  if (!parsed.valid) return parsed.error || 'Invalid pattern.';
  return '';
}
